import { Sentry, logger, startSpan } from '../../lib/sentry'
import { supabase } from '../../lib/supabase'

import { SearchQuery } from './types'

// Load queries from Supabase
export async function loadQueries(
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setError: React.Dispatch<React.SetStateAction<string | null>>,
  setQueries: React.Dispatch<React.SetStateAction<SearchQuery[]>>
) {
  setLoading(true)
  setError(null)

  // Set timeout to prevent infinite loading
  const loadingTimeout = setTimeout(() => {
    logger.warn('Search queries loading timeout after 30 seconds')
    setError('Loading timeout. Please refresh the page.')
    setLoading(false)
  }, 30000)

  try {
    const data = await startSpan(
      {
        op: 'db.query',
        name: 'GET /search_queries',
      },
      async (span: { setAttribute?: (key: string, value: string | number) => void } | null) => {
        const { data, error: fetchError } = await supabase
          .from('search_queries')
          .select('*')
          .order('last_scraped_at', { ascending: false, nullsFirst: false })
          .order('created_at', { ascending: false })

        if (fetchError) {
          logger.error('Error loading search queries', {
            error: fetchError.message,
            code: fetchError.code,
          } as Record<string, unknown>)
          Sentry.captureException(new Error(fetchError.message), {
            extra: { code: fetchError.code },
          })
          if (span) {
            span.setAttribute('error', true)
            span.setAttribute('error_message', fetchError.message)
          }
          setError(fetchError.message)
          setLoading(false)
          return null
        }

        if (span) {
          span.setAttribute('queries_count', data?.length || 0)
        }
        return data
      }
    )

    if (data) {
      const queriesWithStats = await Promise.all(
        data.map(async (query: SearchQuery) => {
          // Get failed jobs count
          const { count: failedCount } = await supabase
            .from('failed_job_attempts')
            .select('*', { count: 'exact', head: true })
            .eq('search_query_id', query.id)
          
          // Get relevance statistics
          const { data: relevanceData } = await supabase
            .from('jobs')
            .select('relevance_score, relevance_verdict')
            .eq('search_query_id', query.id)
            .not('relevance_score', 'is', null)
          
          let avg_relevance_score: number | null = null
          let relevance_verdict_summary: { fit: number; no_fit: number } | null = null
          
          if (relevanceData && relevanceData.length > 0) {
            // Calculate average score
            const scores = relevanceData
              .map(job => job.relevance_score)
              .filter((score): score is number => score !== null && score !== undefined)
            
            if (scores.length > 0) {
              avg_relevance_score = Math.round(
                scores.reduce((sum, score) => sum + score, 0) / scores.length
              )
            }
            
            // Count verdicts
            const fitCount = relevanceData.filter(job => job.relevance_verdict === 'Fit').length
            const noFitCount = relevanceData.filter(job => job.relevance_verdict === 'No Fit').length
            
            if (fitCount > 0 || noFitCount > 0) {
              relevance_verdict_summary = {
                fit: fitCount,
                no_fit: noFitCount
              }
            }
          }
          
          return {
            ...query,
            failed_jobs_count: failedCount || 0,
            avg_relevance_score,
            relevance_verdict_summary
          }
        })
      )
      setQueries(() => queriesWithStats)
      logger.info('Search queries loaded', { count: queriesWithStats.length } as Record<string, unknown>)
    }
  } catch (err: unknown) {
    clearTimeout(loadingTimeout)
    const errorMessage = err instanceof Error ? err.message : String(err)
    logger.error('Unexpected error loading search queries', { error: errorMessage } as Record<string, unknown>)
    if (err instanceof Error) {
      Sentry.captureException(err)
    }
    const errorMsg = errorMessage || 'Failed to load search queries'
    setError(errorMsg)
  } finally {
    clearTimeout(loadingTimeout)
    setLoading(false)
  }
}
